import axios from "axios";
import React, { useEffect, useState } from "react";
import {
  Container,
  Table,
  Spinner,
  Row,
  Col,
  Form,
  Button,
  Modal,
  Nav,
  Alert,
  Pagination,
  Dropdown,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { Search, X } from "lucide-react";
import * as XLSX from "xlsx";
import { getActionPermissions } from "../../../Utils/ActionPermissionChecker";
import { showMyAlert } from "../../../Utils/alertService";
import { doAuditLog } from "../../../Utils/saveHistoryAction";

const EmployeeManagement = () => {
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({
    full_name: "",
    email: "",
    mobile_number: "",
    employee_id: "",
    role: "",
    approval_status: "",
  });
  const [activeTab, setActiveTab] = useState("active");
  const [successMessage, setSuccessMessage] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [popupVariant, setPopupVariant] = useState("success");
  const [startDateFilter, setStartDateFilter] = useState("");
  const [endDateFilter, setEndDateFilter] = useState("");

    const companyKey = sessionStorage.getItem("companyKey");


  const userRole = sessionStorage.getItem("role");
  const { canView, canAdd, canEdit, canDelete } = getActionPermissions(userRole);

  useEffect(() => {
    const checkAuth = () => {
      const userId = sessionStorage.getItem("userMobile");
      if (!userId) {
        navigate("/signIn");
      }
    };

    checkAuth();
  }, [navigate]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          "https://api.dremerz.net/api/DZ_8922_Payroll_PayrollRegistration_PayrollRegistrationUsers"
        );
        const data = response.data;
        const filteredData = data.filter((d)=>d.companyKey === companyKey);
        setUsers(filteredData.reverse());
        setFilteredUsers(data);
        setCurrentPage(1);
      } catch (error) {
        console.error("Error fetching users:", error);
        setError("Failed to load users. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const filterUsers = () => {
    let filtered = [...users];

    if (activeTab === "active") {
      filtered = filtered.filter(
        (user) =>
          user.approval_status === true ||
          user.approval_status === "true" ||
          user.approval_status === "1"
      );
    } else if (activeTab === "inactive") {
      filtered = filtered.filter(
        (user) =>
          user.approval_status === false ||
          user.approval_status === "false" ||
          user.approval_status === "0"
      );
    }

    // Date filter for approval_date
    if (startDateFilter || endDateFilter) {
      filtered = filtered.filter((user) => {
        if (!user.approval_date || user.approval_date === "null") return false;

        try {
          // Parse "DD-MM-YYYY" format
          const [day, month, year] = user.approval_date.split('-');
          const userDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));

          let includeUser = true;

          if (startDateFilter) {
            const startDate = new Date(startDateFilter);
            startDate.setHours(0, 0, 0, 0);
            includeUser = includeUser && userDate >= startDate;
          }

          if (endDateFilter) {
            const endDate = new Date(endDateFilter);
            endDate.setHours(23, 59, 59, 999);
            includeUser = includeUser && userDate <= endDate;
          }

          return includeUser;
        } catch (error) {
          console.error('Error parsing date:', user.approval_date, error);
          return false;
        }
      });
    }

    if (searchQuery.trim() !== "") {
      filtered = filtered.filter((user) => {
        const searchableFields = [
          user.full_name || "",
          user.email || "",
          user.mobile_number || "",
          user.employee_id || "",
          user.role || "",
          user.id?.toString() || "",
          user.approval_date || ""
        ];

        return searchableFields.some((field) =>
          field.toLowerCase().includes(searchQuery.toLowerCase())
        );
      });
    }

    setFilteredUsers(filtered);
    setCurrentPage(1);
  };

  useEffect(() => {
    filterUsers();
  }, [users, activeTab, searchQuery, startDateFilter, endDateFilter]);

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const handleDeleteClick = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {

      await axios.delete(
        `https://api.dremerz.net/api/DZ_8922_Payroll_PayrollRegistration_PayrollRegistrationUsers/${selectedUser.id}`
      );

      doAuditLog("Delete", "Active / Inactive", selectedUser, "");

      const updatedUsers = users.filter((user) => user.id !== selectedUser.id);
      setUsers(updatedUsers);
      setShowDeleteModal(false);
      setSuccessMessage("");
      showMyAlert({
        type: "success",              // "success" | "danger" | "warning" | "info"
        title: "Deleted",
        message: `User "${selectedUser.full_name}" deleted successfully`,
        duration: 2500,               // auto-close in 2.5s
      });
      setTimeout(() => setShowPopup(false), 3000);
    } catch (error) {
      console.error("Error deleting user:", error);
    }
  };

  const handleEditClick = (user) => {
    setSelectedUser(user);
    setEditFormData({
      full_name: user.full_name,
      email: user.email,
      mobile_number: user.mobile_number,
      employee_id: user.employee_id,
      role: user.role,
      approval_status: String(user.approval_status),
    });
    setShowEditModal(true);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleEditSubmit = async () => {
    try {
      // Store old data before making changes
      const oldData = { ...selectedUser };

      const getCurrentDate = () => {
        const today = new Date();
        const day = String(today.getDate()).padStart(2, "0");
        const month = String(today.getMonth() + 1).padStart(2, "0");
        const year = today.getFullYear();
        return `${day}-${month}-${year}`;
      };

      // Check if approval status is being changed to "true"
      const isBeingApproved =
        (editFormData.approval_status === "true" ||
          editFormData.approval_status === true) &&
        selectedUser.approval_status !== true &&
        selectedUser.approval_status !== "true";

      let dataToSend = {
        ...editFormData,
        approval_status: String(editFormData.approval_status),
        approval_date: getCurrentDate(),
      };

      // If user is being approved, fetch payslip labels and add them to the payload
      if (isBeingApproved) {
        try {
          const response = await axios.get(
            "https://api.dremerz.net/api/DZ_8922_EmpAttendance_Payslip_PayslipData_PayslipLabelsList"
          );

          // Process both Earnings and Deductions sections into array of objects in the requested format
          const processSection = (sectionName) => {
            return response.data
              .filter((item) => item.sectionName === sectionName)
              .map((item) => ({
                [item.value]: "true", // Creates object with label as key and "true" as value
              }));
          };

          // Get earnings and deductions as arrays
          const earnings = processSection("Earnings");
          const deductions = processSection("Deductions");

          // Add both to the data being sent as stringified JSON arrays
          dataToSend = {
            ...dataToSend,
            earnings: JSON.stringify(earnings),
            deductions: JSON.stringify(deductions),
          };
        } catch (error) {
          console.error("Error fetching payslip labels:", error);
          // Continue with the update even if fetch fails
        }
      }

      const newData = { ...selectedUser, ...dataToSend };

      await axios.put(
        `https://api.dremerz.net/api/DZ_8922_Payroll_PayrollRegistration_PayrollRegistrationUsers/${selectedUser.id}`,
        dataToSend
      );

      doAuditLog("Update", "Active / Inactive", selectedUser, newData);

      const updatedUsers = users.map((user) =>
        user.id === selectedUser.id ? { ...user, ...editFormData } : user
      );
      setUsers(updatedUsers);
      setShowEditModal(false);
      showMyAlert({
  type: "success",              // "success" | "danger" | "warning" | "info"
  title: "Updated",
  message: `User "${selectedUser.full_name}" updated successfully`,
  duration: 2500,               // auto-close in 2.5s
});
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error updating user:", error);
      setError("Failed to update user. Please try again.");
    }
  };

  const handleInactiveClick = async () => {
    try {
      // Store old data before making changes
      const oldData = { ...selectedUser };

      const getCurrentDate = () => {
        const today = new Date();
        const day = String(today.getDate()).padStart(2, "0");
        const month = String(today.getMonth() + 1).padStart(2, "0");
        const year = today.getFullYear();
        return `${day}-${month}-${year}`;
      };

      const dataToSend = {
        ...editFormData,
        approval_status: "false", // Set to pending
        approval_date: getCurrentDate(),
      };

      const newData = { ...selectedUser, ...dataToSend };

      await axios.put(
        `https://api.dremerz.net/api/DZ_8922_Payroll_PayrollRegistration_PayrollRegistrationUsers/${selectedUser.id}`,
        dataToSend
      );

      doAuditLog("Update", "Active / Inactive", selectedUser, newData);

      const updatedUsers = users.map((user) =>
        user.id === selectedUser.id
          ? { ...user, approval_status: "false" }
          : user
      );
      setUsers(updatedUsers);
      setShowEditModal(false);
            showMyAlert({
  type: "success",              // "success" | "danger" | "warning" | "info"
  title: "Updated",
  message: `User "${selectedUser.full_name}" marked as inactive successfully`,
  duration: 2500,               // auto-close in 2.5s
});
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error marking user as inactive:", error);
      setError("Failed to mark user as inactive. Please try again.");
    }
  };

  const getApprovalStatus = (status) => {
    const isApproved = status === true || status === "true" || status === "1";
    return isApproved ? (
      "Active"
    ) : (
      "Inactive"
    );
  };

  const clearError = () => {
    setError(null);
  };

  const handleDownloadPDF = () => {
    const tableColumn = [
      "#",
      "Full Name",
      "Email ID",
      "Mobile Number",
      "Employee ID",
      "Role",
      "Status",
    ];
    const tableRows = currentItems.map((user, index) => [
      indexOfFirstItem + index + 1,
      user.full_name || "N/A",
      user.email || "N/A",
      user.mobile_number || "N/A",
      user.employee_id || "N/A",
      user.role || "N/A",
      user.approval_status === true ||
        user.approval_status === "true" ||
        user.approval_status === "1"
        ? "Active"
        : "Inactive",
    ]);
    if (tableRows.length === 0) {
            showMyAlert({
  type: "info",              // "success" | "danger" | "warning" | "info"
  title: "No Data",
  message: "No data to download for the selected criteria.",
  duration: 2500,               // auto-close in 2.5s
});
      return;
    }
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text("Employee Management", 14, 16);
    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: 22,
      styles: { fontSize: 10, cellPadding: 2 },
      headStyles: { fillColor: [41, 128, 185], textColor: 255 },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      margin: { left: 14, right: 14 },
    });
    doc.save("Employee_Management.pdf");
  };

  const handleDownloadExcel = () => {
    if (filteredUsers.length === 0) {
            showMyAlert({
  type: "info",              // "success" | "danger" | "warning" | "info"
  title: "No Data",
  message: "No employee data available to export.",
  duration: 2500,               // auto-close in 2.5s
});
      return;
    }

    const excelData = filteredUsers.map((user, index) => ({
      "S.I": index + 1,
      "Full Name": user.full_name || "N/A",
      "Email ID": user.email || "N/A",
      "Mobile Number": user.mobile_number || "N/A",
      "Employee ID": user.employee_id || "N/A",
      Role: user.role || "N/A",
      Status:
        user.approval_status === true ||
          user.approval_status === "true" ||
          user.approval_status === "1"
          ? "Active"
          : "Inactive",
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Employees");

    worksheet["!cols"] = [
      { wch: 5 },
      { wch: 25 },
      { wch: 30 },
      { wch: 15 },
      { wch: 15 },
      { wch: 15 },
      { wch: 10 },
    ];

    XLSX.writeFile(workbook, "Employee_Management.xlsx");
  };


  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredUsers.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Handle items per page change
  const handleItemsPerPageChange = (count) => {
    setItemsPerPage(count);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const isMobile = window.innerWidth < 768;

  return (
    <Container className="mt-3">

      {/* Header Section */}
      <div className="row align-items-center mb-2 border rounded bg-light py-2 mx-1">
        <div className="col-12 col-md-6 mb-2 mb-md-0">
          <h5 className="mb-0 d-flex align-items-center justify-content-center justify-content-md-start gap-2">
            Active/Inactive Employee
          </h5>
        </div>
        <div className="col-12 col-md-6 d-flex justify-content-center justify-content-md-end gap-2 text-md-end">
          <Button
            variant="outline-success"
            size="sm"
            title="Download Excel"
            onClick={handleDownloadExcel}
          >
            Export Excel
          </Button>

          <Button
            variant="outline-danger"
            size="sm"
            title="Download PDF"
            onClick={handleDownloadPDF}
          >
            Export PDF
          </Button>
        </div>

      </div>

      {/* Search Section */}
      <div className="row align-items-center mb-3 border rounded bg-light py-2 mx-1">
        {/* Search */}
        <div className="col-12 col-md-4">
          <Form.Group className="mb-0">
            <Form.Label className="small text-muted mb-1 d-block">Search</Form.Label>
            <div className="input-group input-group-sm">
              <span className="input-group-text bg-light border-end-0">
                <Search size={16} className="text-muted" />
              </span>
              <input
                type="text"
                className="form-control border-start-0 border-end-0"
                placeholder="Search here..."
                value={searchQuery}
                onChange={handleSearch}
              />
            </div>
          </Form.Group>
        </div>

        {/* Date Filters */}
        <div className="col-12 col-md-6 mt-2 mt-md-0">
          <Row className="g-2 align-items-end">
            <div className="col-md-5">
              <Form.Group className="mb-0">
                <Form.Label className="small text-muted mb-1 d-block">Approved From</Form.Label>
                <Form.Control
                  type="date"
                  size="sm"
                  value={startDateFilter}
                  onChange={(e) => setStartDateFilter(e.target.value)}
                />
              </Form.Group>
            </div>

            <div className="col-md-5">
              <Form.Group className="mb-0">
                <Form.Label className="small text-muted mb-1 d-block">Approved To</Form.Label>
                <Form.Control
                  type="date"
                  size="sm"
                  value={endDateFilter}
                  onChange={(e) => setEndDateFilter(e.target.value)}
                  min={startDateFilter}
                />
              </Form.Group>
            </div>

            {(startDateFilter || endDateFilter || searchQuery) && (
              <div className="col-md-2">
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => {
                    setStartDateFilter("");
                    setEndDateFilter("");
                    setSearchQuery("");
                  }}
                  className="form-control"
                >
                  Clear
                </Button>
              </div>
            )}
          </Row>
        </div>
      </div>

      {/* Pagination Section */}
      {totalPages > 1 && (
        <div className="row align-items-center justify-content-between mb-2 p-2">


          {/* Items per page selector */}
          <div className="col-5 col-md-auto mb-2 mb-md-0">
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted small">Paginate</span>
              <Dropdown>
                <Dropdown.Toggle
                  size="sm"
                  variant="outline-secondary"
                  id="dropdown-items-per-page"
                >
                  {itemsPerPage}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {[5, 10, 20, 50, 100].map((count) => (
                    <Dropdown.Item
                      key={count}
                      onClick={() => handleItemsPerPageChange(count)}
                      active={itemsPerPage === count}
                    >
                      {count}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>

          <div className="col-7 col-md-auto">
            <Pagination size="sm" className="mb-0 justify-content-center justify-content-md-end">
              <Pagination.First
                onClick={() => paginate(1)}
                disabled={currentPage === 1}
              />
              <Pagination.Prev
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
              />

              {isMobile ? (
                <>
                  <Pagination.Item active>{currentPage}</Pagination.Item>
                  {totalPages > 1 && (
                    <>
                      <Pagination.Ellipsis disabled />
                      <Pagination.Item onClick={() => paginate(totalPages)}>
                        {totalPages}
                      </Pagination.Item>
                    </>
                  )}
                </>
              ) : (
                (() => {
                  const items = [];
                  const maxVisiblePages = 5;
                  let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                  let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

                  if (endPage - startPage + 1 < maxVisiblePages) {
                    startPage = Math.max(1, endPage - maxVisiblePages + 1);
                  }

                  if (startPage > 1) {
                    items.push(<Pagination.Ellipsis key="start-ellipsis" />);
                  }

                  for (let number = startPage; number <= endPage; number++) {
                    items.push(
                      <Pagination.Item
                        key={number}
                        active={number === currentPage}
                        onClick={() => paginate(number)}
                      >
                        {number}
                      </Pagination.Item>
                    );
                  }

                  if (endPage < totalPages) {
                    items.push(<Pagination.Ellipsis key="end-ellipsis" />);
                  }

                  return items;
                })()
              )}

              <Pagination.Next
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
              />
              <Pagination.Last
                onClick={() => paginate(totalPages)}
                disabled={currentPage === totalPages}
              />
            </Pagination>
          </div>
        </div>
      )}
      {error && (
        <Alert
          variant="danger"
          onClose={clearError}
          dismissible
          className="mb-3"
        >
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert variant="success" className="mb-3">
          {successMessage}
        </Alert>
      )}

      {/* Employee Status Tabs */}
      <div className="row align-items-center rounded">
        <div className="col-12">
          <Nav variant="tabs" defaultActiveKey="active" className="border-0">
            <Nav.Item>
              <Nav.Link
                eventKey="active"
                active={activeTab === "active"}
                onClick={() => handleTabChange("active")}
                className="py-2"
              >
                Active Employees
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                eventKey="inactive"
                active={activeTab === "inactive"}
                onClick={() => handleTabChange("inactive")}
                className="py-2"
              >
                Inactive Employees
              </Nav.Link>
            </Nav.Item>
          </Nav>
        </div>
      </div>

      <div className="table-responsive">
        {isMobile ? (
          currentItems.length > 0 ? (
            currentItems.map((user, index) => (
              <div key={user.id} className="mb-3">
                <div
                  className="card shadow-sm"
                  style={{ textAlign: "left", borderRadius: "12px" }}
                >
                  <div className="card-body table-responsive">
                    <table className="table table-striped table-bordered mb-2">
                      <tbody>
                        <tr>
                          <td className="fw-bold">SI</td>
                          <td>{filteredUsers.length - (indexOfFirstItem + index)}</td>
                        </tr>
                        <tr>
                          <td className="fw-bold">Full Name</td>
                          <td>{user.full_name}</td>
                        </tr>
                        <tr>
                          <td className="fw-bold">Email ID</td>
                          <td>{user.email || "N/A"}</td>
                        </tr>
                        <tr>
                          <td className="fw-bold">Mobile Number</td>
                          <td>{user.mobile_number}</td>
                        </tr>
                        <tr>
                          <td className="fw-bold">Employee ID</td>
                          <td>{user.employee_id || "N/A"}</td>
                        </tr>
                        <tr>
                          <td className="fw-bold">Role</td>
                          <td>{user.role}</td>
                        </tr>
                        <tr>
                          <td className="fw-bold">Created At</td>
                          <td>{user.approval_date}</td>
                        </tr>
                        <tr>
                          <td className="fw-bold">Status</td>
                          <td>
                            {getApprovalStatus(user.approval_status)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                   { (canDelete || canEdit) && <div className="d-flex justify-content-end gap-2">

                    { canEdit && <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleEditClick(user)}
                        disabled={userRole !== "superadmin"}
                      >
                        Edit
                      </Button>}
                    { canDelete &&  <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDeleteClick(user)}
                        disabled={userRole !== "superadmin"}
                      >
                        Delete
                      </Button>}

                    </div>}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-4">
              {searchQuery
                ? "No employees found matching your search criteria."
                : `No ${activeTab} employees found.`}
            </div>
          )
        ) : (
          <Table responsive striped bordered hover className="mb-0 mt-3">
            <thead className="">
              <tr>
                <th id="text-white-important">S.I</th>
                <th id="text-white-important">Full Name</th>
                <th id="text-white-important">Email ID</th>
                <th id="text-white-important">Mobile Number</th>
                <th id="text-white-important">Employee ID</th>
                <th id="text-white-important">Role</th>
                <th id="text-white-important">Created At</th>
                <th id="text-white-important">Status</th>
              {(canDelete|| canEdit) && <th id="text-white-important">Actions</th>}
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="text-center py-4">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2 mb-0">Loading employees...</p>
                  </td>
                </tr>
              ) : currentItems.length > 0 ? (
                currentItems.map((user, index) => (
                  <tr key={user.id}>
                    <td>{filteredUsers.length - (indexOfFirstItem + index)}</td>
                    <td>{user.full_name}</td>
                    <td>{user.email || "N/A"}</td>
                    <td>{user.mobile_number}</td>
                    <td>{user.employee_id || "N/A"}</td>
                    <td>{user.role}</td>
                    <td>{user.approval_date}</td>
                    <td>{getApprovalStatus(user.approval_status)}</td>
                   {(canDelete || canEdit) && <td className="d-flex gap-2">

                  { canEdit &&   <Button
                        variant="outline-primary"
                        onClick={() => handleEditClick(user)}
                        disabled={userRole !== "superadmin"}
                        size="sm"
                      >
                        Edit
                      </Button>}
                    { canDelete && <Button
                        variant="outline-danger"
                        onClick={() => handleDeleteClick(user)}
                        disabled={userRole !== "superadmin"}
                        size="sm"
                      >
                        Delete
                      </Button>
}
                    </td>}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="text-center py-4">
                    {searchQuery
                      ? "No employees found matching your search criteria."
                      : `No ${activeTab} employees found.`}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        )}
      </div>


      {/* Showing entries info */}
      <div className="col-12 mb-2 text-end">
        <span className="text-muted">
          Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredUsers.length)} of {filteredUsers.length} entries
        </span>
      </div>
      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete the following employee?</p>
          <ul className="list-unstyled">
            <li>
              <strong>Name:</strong> {selectedUser?.full_name}
            </li>
            <li>
              <strong>Mobile:</strong> {selectedUser?.mobile_number}
            </li>
            <li>
              <strong>Role:</strong> {selectedUser?.role}
            </li>
          </ul>
          <p className="text-danger">This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteConfirm}>
            Confirm Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit User Modal */}
      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Employee: {selectedUser?.full_name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Full Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="full_name"
                    value={editFormData.full_name}
                    onChange={handleEditChange}
                    disabled
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={editFormData.email}
                    onChange={handleEditChange}
                    disabled
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Mobile Number</Form.Label>
                  <Form.Control
                    type="text"
                    name="mobile_number"
                    value={editFormData.mobile_number}
                    onChange={handleEditChange}
                    disabled
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Employee ID</Form.Label>
                  <Form.Control
                    type="text"
                    name="employee_id"
                    value={editFormData.employee_id}
                    onChange={handleEditChange}
                    disabled
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    name="approval_status"
                    value={editFormData.approval_status}
                    onChange={handleEditChange}
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleEditSubmit}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showPopup}
        centered
        backdrop="static"
        keyboard={false}
        onHide={() => setShowPopup(false)}
      >
        <Modal.Body className="text-center">
          <Alert variant={popupVariant} className="mb-0">
            {popupMessage}
          </Alert>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default EmployeeManagement;
